import { formDialogFieldStyles, formDialogLabelStyles } from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Box, Button, FormLabel, IconButton, InputAdornment, TextField } from '@mui/material';
import { Check, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { passwordRules } from './passwordRules';

// Letters and digits that cannot be taken for one another (no l, I, O or 0), and symbols the
// server counts, but not "^": a dead key on German, French and Turkish keyboards, where "^a" is "â".
const lowerCase = 'abcdefghijkmnopqrstuvwxyz';
const upperCase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const digits = '123456789';
const symbols = '@#%*>$?/[]=+';

const randomIndex = (length: number) => crypto.getRandomValues(new Uint32Array(1))[0] % length;
const pick = (characters: string) => characters.charAt(randomIndex(characters.length));

/**
 * Eight characters, with a capital, a small letter, a digit and a symbol always among them, the
 * rest from all four, in a random order.
 */
const generatePassword = () => {
  const all = lowerCase + upperCase + digits + symbols;
  const characters = [lowerCase, upperCase, digits, symbols, all, all, all, all].map(pick);

  // Shuffled, so that each kind does not always stand in the same place.
  for (let at = characters.length - 1; at > 0; at--) {
    const other = randomIndex(at + 1);
    [characters[at], characters[other]] = [characters[other], characters[at]];
  }

  return characters.join('');
};

interface PasswordFieldProps {
  id: string;
  label: string;
  required: boolean;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
  /** Said under the field while it has no error: why the password is not used now, say. */
  note?: string;
  /** The rules are listed under the field: always for a new account, once something is typed on an edit. */
  showRules: boolean;
  disabled: boolean;
}

/**
 * A password for somebody else's account: shown or hidden at will, made here if asked, and
 * checked against the server's rules as it is typed, so a refusal is never the first word of them.
 */
const PasswordField = ({
  id, label, required, value, onChange, onBlur, error, note, showRules, disabled,
}: PasswordFieldProps) => {
  const { t } = useLocalization();
  const [visible, setVisible] = useState(false);

  // A password made here is shown, since it has to be handed to its owner.
  const generate = () => {
    onChange(generatePassword());
    setVisible(true);
  };

  return (
    <Box>
      <FormLabel htmlFor={id} required={required} sx={formDialogLabelStyles}>{label}</FormLabel>
      <TextField
        id={id}
        fullWidth
        size="small"
        type={visible ? 'text' : 'password'}
        // Not the admin's own password, which the browser would otherwise offer here.
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        error={Boolean(error)}
        helperText={error ?? note}
        disabled={disabled}
        sx={formDialogFieldStyles}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={t(visible ? 'User_Password_Hide' : 'User_Password_Show')}
                  onClick={() => setVisible(!visible)}
                  disabled={disabled}
                >
                  {visible ? <EyeOff size={16} /> : <Eye size={16} />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      {/* Under the field and after it, so the keyboard comes to the field first. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 1 }}>
        {showRules && (
          <Box component="ul" sx={rulesStyles}>
            {passwordRules.map((rule) => (
              <li key={rule.label} className={rule.test(value) ? 'met' : undefined}>
                <Check size={14} aria-hidden />
                {t(rule.label)}
              </li>
            ))}
          </Box>
        )}
        <Button size="small" onClick={generate} disabled={disabled} sx={generateStyles}>
          {t('User_Password_Generate')}
        </Button>
      </Box>
    </Box>
  );
};

// At the end of its line, with the rules beside it or without them.
const generateStyles = {
  ml: 'auto',
  flexShrink: 0,
  minWidth: 0,
  px: 1,
  py: 0.25,
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
};

/** A rule not met yet is quiet, with a faint mark; a rule met turns green. */
const rulesStyles = {
  flex: 1,
  listStyle: 'none',
  m: 0,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  fontSize: 12.5,
  color: 'text.secondary',
  '& li': { display: 'flex', alignItems: 'center', gap: 0.75 },
  '& li svg': { opacity: 0.35 },
  '& li.met': { color: 'success.light' },
  '& li.met svg': { opacity: 1 },
};

export default PasswordField;
